import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ResilientInscriptionMedia } from "../../components/ResilientInscriptionMedia";
import { fetchAuctions } from "../../lib/api";
import type { AuctionRecord } from "../../lib/auctions";

/** Real catalogue content only. Never fall back to invented inventory or prices. */
export function AuctionSpotlight() {
  const [auctions, setAuctions] = useState<AuctionRecord[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const page = await fetchAuctions(0, undefined, "open");
        if (active) {
          setAuctions(page.platform === "artefact" ? page.auctions : []);
          setState("ready");
        }
      } catch {
        if (active) { setAuctions([]); setState("error"); }
      } finally {
        if (active) { setNow(Date.now()); timer = setTimeout(() => { void load(); }, 30_000); }
      }
    };
    void load();
    // Expire a featured lot even when a refresh is slow or unavailable.
    const clock = setInterval(() => setNow(Date.now()), 1_000);
    return () => { active = false; clearTimeout(timer); clearInterval(clock); };
  }, []);
  const eligible = auctions.filter(auction => auction.platform === "artefact"
    && auction.status === "open" && auction.settlementState === "open" && auction.endsAt > now);
  const auction = eligible.find(item => item.startsAt <= now) ?? eligible[0];
  return <section className="ar-spotlight" aria-label="From the auction room">
    {auction ? <Link className="ar-spotlight-link" to={`/auctions/${auction.id}`}>
      <div className="ar-spotlight-art">
        <ResilientInscriptionMedia inscriptionId={auction.inscriptionId} label={auction.title} className="ar-media" />
      </div>
      <div className="ar-spotlight-caption">
        <div><small>{auction.startsAt > now ? "Upcoming auction" : "At auction"}</small><h2>{auction.title}</h2></div>
        <span>View auction <ArrowRight aria-hidden="true" /></span>
      </div>
    </Link> : <div className="ar-spotlight-empty" role="status">
      <p className="ar-eyebrow">The auction room</p>
      <h2>{state === "loading" ? "Loading the current selection…" : state === "error" ? "The selection is unavailable." : "The next original awaits."}</h2>
      <p>{state === "error" ? "Visit the auction room to try again." : state === "ready" ? "Discover past works, or bring your own to auction." : "Original works, direct from the catalogue."}</p>
      <Link className="ar-text-link" to="/auctions">Explore auctions <ArrowRight aria-hidden="true" /></Link>
    </div>}
  </section>;
}
